import React from 'react';
import { View } from 'react-native';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Text, TypographyHeading, TypographyParagraph } from '..';
import { VisualDirectionPhotoGrid } from '../image';

const hasPhotos = item => Boolean(item?.visualDirectionPhotos?.length || item?.visualDirectionPhotoUrls?.length);

const AccessStep = ({
	addButtonLabel,
	canEdit,
	directions,
	emptyText,
	name,
	onAddPhoto,
	onRemovePhoto,
	onUpdatePhotoComment,
	photoUrls,
	photos,
	showDirectionsText,
	showEmptyPhotos,
	title,
}) => {
	const styles = useThemedStyles(createStyles);
	const showPhotos = Boolean(canEdit || photos?.length || photoUrls?.length || showEmptyPhotos);

	return (
		<View style={styles.step}>
			<TypographyHeading
				subheadingSmallSubtle={true}
				label={title}
			/>
			{name ? <Text style={styles.name}>{name}</Text> : null}
			{showDirectionsText && (
				directions ? (
					<TypographyParagraph style={styles.directions} text={directions} />
				) : (
					<Text style={styles.emptyText}>{emptyText}</Text>
				)
			)}
			{showPhotos && (
				<VisualDirectionPhotoGrid
					addButtonLabel={addButtonLabel}
					canEdit={canEdit}
					emptyText={canEdit || showEmptyPhotos ? 'No visual direction photos.' : ''}
					onAddPhoto={onAddPhoto}
					onRemovePhoto={onRemovePhoto}
					onUpdatePhotoComment={onUpdatePhotoComment}
					photos={photos}
					photoUrls={photoUrls}
					style={styles.photoGrid}
				/>
			)}
		</View>
	);
};

const AccessDirectionsSection = ({
	canEditLocation = false,
	canEditPlace = false,
	location,
	locationTitle,
	onAddLocationPhoto,
	onAddPlacePhoto,
	onRemoveLocationPhoto,
	onRemovePlacePhoto,
	onUpdateLocationPhotoComment,
	onUpdatePlacePhotoComment,
	place,
	placeTitle,
	showEmpty = false,
	showLocation = true,
	showPlace = true,
	showDirectionsText = true,
	showEmptyPhotos = false,
	showTitle = true,
	title = 'Directions',
}) => {
	const styles = useThemedStyles(createStyles);
	const hasVisibleContent = item => Boolean((showDirectionsText && item?.directions) || hasPhotos(item));
	const shouldShowLocationStep = Boolean(showLocation && (hasVisibleContent(location) || canEditLocation || (showEmpty && location)));
	const shouldShowPlaceStep = Boolean(showPlace && (hasVisibleContent(place) || canEditPlace || (showEmpty && place)));
	const resolvedLocationTitle = locationTitle || 'Location directions';
	const resolvedPlaceTitle = placeTitle || 'Place directions';
	const shouldRender = shouldShowLocationStep || shouldShowPlaceStep;

	if (!shouldRender) {
		return null;
	}

	return (
		<View style={styles.wrapper}>
			{showTitle && <Text style={styles.sectionHeading}>{title}</Text>}
			{shouldShowLocationStep && (
				<AccessStep
					title={resolvedLocationTitle}
					name={location?.name}
					directions={location?.directions}
					emptyText={location ? 'No location directions.' : 'No location selected.'}
					addButtonLabel="Add location photo"
					canEdit={Boolean(location && canEditLocation)}
					onAddPhoto={onAddLocationPhoto}
					onRemovePhoto={onRemoveLocationPhoto}
					onUpdatePhotoComment={onUpdateLocationPhotoComment}
					photos={location?.visualDirectionPhotos || []}
					photoUrls={location?.visualDirectionPhotoUrls || []}
					showDirectionsText={showDirectionsText}
					showEmptyPhotos={showEmptyPhotos}
				/>
			)}
			{shouldShowPlaceStep && (
				<AccessStep
					title={resolvedPlaceTitle}
					name={place?.name}
					directions={place?.directions}
					emptyText={place ? 'No place directions.' : 'No place selected.'}
					addButtonLabel="Add place photo"
					canEdit={Boolean(place && canEditPlace)}
					onAddPhoto={onAddPlacePhoto}
					onRemovePhoto={onRemovePlacePhoto}
					onUpdatePhotoComment={onUpdatePlacePhotoComment}
					photos={place?.visualDirectionPhotos || []}
					photoUrls={place?.visualDirectionPhotoUrls || []}
					showDirectionsText={showDirectionsText}
					showEmptyPhotos={showEmptyPhotos}
				/>
			)}
		</View>
	);
};

const createStyles = theme => ({
	wrapper: {
		marginTop: 12,
	},
	sectionHeading: {
		fontSize: 18,
		marginTop: 5,
		marginBottom: 4,
		color: theme.text1,
	},
	step: {
		borderWidth: 1,
		borderColor: theme.border1,
		borderRadius: 6,
		padding: 10,
		marginTop: 8,
		backgroundColor: theme.mainBg2,
	},
	name: {
		color: theme.text1,
		fontWeight: '600',
		marginBottom: 4,
	},
	directions: {
		fontSize: 15,
		lineHeight: 21,
	},
	emptyText: {
		color: theme.text2,
	},
	photoGrid: {
		marginTop: 8,
	},
});

export default AccessDirectionsSection;
